import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  CreatePlanFromWizardRequestDto,
  PreviewPlanFromWizardResponseDto,
} from '@evimo/client/models';
import { usePlans } from '~/app/hooks/maintenance/usePlans';
import { useWizardCheckConflicts } from '~/app/hooks/maintenance/usePlanAssets';
import type { WizardData } from '~/app/components/maintenance/preventive/wizard/types';
import {
  applyConflictDecisionsToWizard,
  canAdvanceAfterPreview,
  canProceedStepAssets,
  normalizeDatePickerString,
  unresolvedPreviewConflicts,
} from '~/app/components/maintenance/preventive/wizard/StepAssets/utils';
import { wizardTasksHaveTriggers } from '~/app/components/maintenance/preventive/wizard/StepTasks/utils';
import { buildWizardPayload, INITIAL_WIZARD_DATA } from '../utils';

export function usePlanWizardPage() {
  const { t } = useTranslation('maintenance');
  const navigate = useNavigate();
  const {
    createPlanFromWizard,
    isCreatingWizard,
    previewPlanFromWizard,
    isPreviewingWizard,
  } = usePlans();
  const { checkWizardConflicts } = useWizardCheckConflicts();
  const [current, setCurrent] = useState(0);
  const [data, setData] = useState<WizardData>(INITIAL_WIZARD_DATA);
  const dataRef = useRef(data);
  dataRef.current = data;
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [isAdvancingAssets, setIsAdvancingAssets] = useState(false);
  const [reviewPreview, setReviewPreview] = useState<
    PreviewPlanFromWizardResponseDto | undefined
  >(undefined);
  const [reviewPreviewError, setReviewPreviewError] = useState<string | null>(
    null,
  );

  const handleChange = useCallback((patch: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...patch }));
  }, []);

  const canProceed = useMemo(() => {
    if (current === 0)
      return data.code.trim() !== '' && data.name.trim() !== '';
    if (current === 1) return wizardTasksHaveTriggers(data.tasks);
    if (current === 2) return canProceedStepAssets(data);
    if (current === 3) {
      if (!reviewPreview) return false;
      if (reviewPreview.validationErrors.length > 0) return false;
      return (
        unresolvedPreviewConflicts(
          reviewPreview.conflicts,
          data.replaceAssetIds,
        ).length === 0
      );
    }
    return true;
  }, [current, data, reviewPreview]);

  const advanceToReview = useCallback((patch?: Partial<WizardData>) => {
    if (patch) {
      setData((prev) => ({ ...prev, ...patch }));
    }
    setConflictModalOpen(false);
    setCurrent(3);
  }, []);

  const handleConflictsResolved = useCallback(() => {
    setData((prev) => ({
      ...prev,
      ...applyConflictDecisionsToWizard(prev),
    }));
    setConflictModalOpen(false);
    setCurrent(3);
  }, []);

  const handleNext = useCallback(async () => {
    if (current !== 2) {
      setCurrent((c) => c + 1);
      return;
    }

    if (data.assetIds.length === 0) {
      advanceToReview({
        checkedConflicts: [],
        conflictDecisions: {},
        conflictsChecked: true,
        replaceAssetIds: [],
      });
      return;
    }

    if (!canProceedStepAssets(data)) return;

    if (canAdvanceAfterPreview(data)) {
      advanceToReview(applyConflictDecisionsToWizard(data));
      return;
    }

    setIsAdvancingAssets(true);
    try {
      const conflicts = await checkWizardConflicts({
        assetIds: data.assetIds,
        startDate: normalizeDatePickerString(data.startDate),
      });
      const previewPatch: Partial<WizardData> = {
        checkedConflicts: conflicts,
        conflictDecisions: {},
        conflictsChecked: true,
        replaceAssetIds: [],
      };

      if (conflicts.length === 0) {
        advanceToReview(previewPatch);
        return;
      }

      handleChange(previewPatch);
      setConflictModalOpen(true);
    } catch (err) {
      void message.error(
        err instanceof Error
          ? err.message
          : t('wizard.errors.previewPrepareFailed'),
      );
    } finally {
      setIsAdvancingAssets(false);
    }
  }, [advanceToReview, checkWizardConflicts, current, data, handleChange, t]);

  useEffect(() => {
    if (current !== 3) return;
    let cancelled = false;
    setReviewPreview(undefined);
    setReviewPreviewError(null);
    const snapshot = dataRef.current;
    void (async () => {
      try {
        const result = await previewPlanFromWizard(
          buildWizardPayload(snapshot),
        );
        if (!cancelled) setReviewPreview(result);
      } catch (err) {
        if (!cancelled) {
          setReviewPreview(undefined);
          setReviewPreviewError(
            err instanceof Error
              ? err.message
              : t('wizard.errors.previewPrepareFailed'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [current, previewPlanFromWizard, t]);

  const handleSave = useCallback(async () => {
    try {
      const payload: CreatePlanFromWizardRequestDto = buildWizardPayload(data);
      await createPlanFromWizard(payload);
      void message.success(t('wizard.review.confirm'));
      navigate('/maintenance/preventive');
    } catch (err) {
      void message.error(
        err instanceof Error ? err.message : t('wizard.errors.saveFailed'),
      );
    }
  }, [createPlanFromWizard, data, navigate, t]);

  const handleBack = useCallback(() => {
    if (current === 3) {
      // Force Step 3 Continue to re-run wizard preview after Review.
      handleChange({
        conflictsChecked: false,
        checkedConflicts: [],
        conflictDecisions: {},
        replaceAssetIds: [],
      });
    }
    setCurrent((c) => c - 1);
  }, [current, handleChange]);

  const handleCancel = useCallback(() => {
    navigate('/maintenance/preventive');
  }, [navigate]);

  const stepTitles = useMemo(
    () => [
      t('wizard.steps.general'),
      t('wizard.steps.tasks'),
      t('wizard.steps.assets'),
      t('wizard.steps.review'),
    ],
    [t],
  );

  return {
    current,
    data,
    stepTitles,
    canProceed,
    conflictModalOpen,
    setConflictModalOpen,
    isAdvancingAssets,
    isCreatingWizard,
    isPreviewingWizard,
    reviewPreview,
    reviewPreviewError,
    handleChange,
    handleConflictsResolved,
    handleCancel,
    handleBack,
    handleNext,
    handleSave,
  };
}
